import React, { FC } from 'react';

import { CircleConfirmIcon, CloseCircleIcon } from '../../assets/icons/index';
import { AppFormattedMessage } from '../../components/app-formatted-message/index';
import { useFormat } from '../../hooks/index';
import { ShareClass } from '../../types/share-classes.types';

export type ShareClassItemProps = Pick<
  ShareClass,
  | 'antiDilutionRights'
  | 'conversionRatio'
  | 'creationDate'
  | 'dividendRightEnabled'
  | 'id'
  | 'isUsed'
  | 'liquidityPreferences'
  | 'name'
  | 'stakeholders'
  | 'votingRight'
  | 'votingRightEnabled'
>;

export const ShareClassItem: FC<ShareClassItemProps> = ({
  conversionRatio,
  dividendRightEnabled,
  name,
  stakeholders,
  votingRight,
  votingRightEnabled,
  antiDilutionRights,
  creationDate = new Date(),
  liquidityPreferences,
  isUsed,
}) => {
  const { format } = useFormat();

  return (
    <div className="flex w-full max-w-[432px] flex-col divide-y-[1px] divide-gray-100 rounded-md bg-white shadow-sm">
      <div className="flex w-full items-center justify-between gap-8 p-3">
        <div className="flex flex-col gap-3 overflow-hidden">
          <span className="w-full truncate text-base font-[550] text-gray-700">{name}</span>
          <span className="text-xs font-[450] text-gray-700">
            {format(creationDate, 'dd/MM/yyyy')}
          </span>
        </div>
        {isUsed ? (
          <span className="shrink-0 rounded-2xl bg-forest-50 px-2 py-[2px] text-label-md font-[450] text-forest-600">
            <AppFormattedMessage id="FILTER_IN_USE" />
          </span>
        ) : (
          <span className="shrink-0 rounded-2xl bg-fireside-50 px-2 py-[2px] text-label-md font-[450] text-fireside-600">
            <AppFormattedMessage id="FILTER_NOT_USED" />
          </span>
        )}
      </div>
      <div className="flex w-full items-center justify-between p-3">
        <span className="text-sm font-[450] text-gray-700">
          <AppFormattedMessage id="STAKEHOLDERS" />
        </span>
        <span className="text-sm font-[450] text-gray-700">{stakeholders}</span>
      </div>
      <div className="flex w-full flex-wrap items-center justify-between gap-4 p-3">
        <span className="text-sm font-[450] text-gray-700">
          <AppFormattedMessage id="STAKEHOLDERS" />
        </span>
        <div className="flex gap-3 text-xs font-normal">
          <div className="flex items-center gap-1">
            {dividendRightEnabled ? <CircleConfirmIcon /> : <CloseCircleIcon className="size-4" />}
            <AppFormattedMessage id="DIVIDENDS" />
          </div>

          <div className="flex items-center gap-1">
            {antiDilutionRights ? <CircleConfirmIcon /> : <CloseCircleIcon className="size-4" />}
            <AppFormattedMessage id="ANTI_DILUTION" />
          </div>
        </div>
      </div>
      <div className="flex w-full items-center justify-between p-3">
        <span className="text-sm font-[450] text-gray-700">
          <AppFormattedMessage id="VOTING_RIGHTS" />
        </span>
        <span className="text-sm font-[450] text-gray-700">
          {votingRightEnabled ? votingRight : '-'}
        </span>
      </div>
      <div className="flex w-full items-center justify-between p-3">
        <span className="text-sm font-[450] text-gray-700">
          <AppFormattedMessage id="CONVERSION_RATIO" />
        </span>
        <span className="text-sm font-[450] text-gray-700">
          {conversionRatio ? conversionRatio.toFixed(2) + 'x' : '-'}
        </span>
      </div>
      <div className="flex w-full items-center justify-between p-3">
        <span className="text-sm font-[450] text-gray-700">
          <AppFormattedMessage id="SENIORITY" />
        </span>
        <span className="text-sm font-[450] text-gray-700">
          {liquidityPreferences ? liquidityPreferences?.seniority : '-'}
        </span>
      </div>
      <div className="flex w-full flex-wrap items-center justify-between gap-4 overflow-hidden p-3">
        <span className="text-sm font-[450] text-gray-700">
          <AppFormattedMessage id="LIQUIDITY_PREFERENCES" />
        </span>
        {liquidityPreferences ? (
          <div className="flex w-fit max-w-[150px] flex-col text-wrap break-all text-xs font-normal text-gray-700">
            <span>
              <AppFormattedMessage id="MULTIPLE" />:{liquidityPreferences.multiple}
            </span>
            {liquidityPreferences.participating?.capValue && (
              <span>
                <AppFormattedMessage id="PARTICIPATING_CAP" />:
                {liquidityPreferences.participating?.capValue}
              </span>
            )}
            {liquidityPreferences.participating?.interest && (
              <span>
                <AppFormattedMessage id="INTEREST" />:{liquidityPreferences.participating?.interest}
                %
              </span>
            )}
          </div>
        ) : (
          '-'
        )}
      </div>
    </div>
  );
};
